import React from 'react';
import PropTypes from 'prop-types';

import ResultCard from './ResultCard';
import ResultHeader from './ResultHeader';
import ResultTable from './ResultTable';
import DORButtonDropdown from './dors/DORButtonDropdown';
import { formatDate } from './dateHelper';
import { useClaimsShopSettings } from '../ClaimsShopSettingsContext';
import Text from '../../common/Text';
import PlainButton from '../../common/PlainButton/PlainButton';
import Locale from '../../Translate/Locale';
import ClaimsIcon from '../../../icons/claim.svg';

import './ShipmentCard.scss';
import { ClaimStatus } from '../claims/types';

function ShipmentCard(props) {
  const {
    id,
    creationDate,
    trackingCode,
    customer = {},
    carrier = {},
    lastEvent,
    onClaim,
    claimable,
    claimId,
    dorStatus,
    claimStatus,
    deadlines,
    claimType,
    claimSubmittedAt,
    hasShipmentDetailsAccess,
    availableDORLanguages = [],
    deliveryDate,
  } = props;

  const { isEasyDOREnabled, isDORSigningLinkEnabled } = useClaimsShopSettings();
  const isSomeDOROptionEnabled = isDORSigningLinkEnabled || isEasyDOREnabled;
  const showDORButton = availableDORLanguages.length > 0 && !!deliveryDate && isSomeDOROptionEnabled;
  const typeOfDORButton = isDORSigningLinkEnabled ? 'dor-signing-link' : 'easy-dor';

  const orderUrl = `/parcel-finder/shipments/${id}`;
  const showClaimButton = claimable && !claimId;
  const showClaimedTag = !!claimId;
  const showDeadline = claimStatus === ClaimStatus.DRAFT;
  const lastSubmissionDate = (deadlines && claimType && deadlines[claimType]?.lastSubmissionDate) || null;

  return (
    <ResultCard>
      <ResultHeader
        type="shipment"
        name="Shipment"
        labelForId="Shipment tracking code"
        id={trackingCode}
        lastEvent={lastEvent}
        link={orderUrl}
        claimStatus={claimStatus}
        claimType={claimType}
        claimSubmittedAt={claimSubmittedAt}
        dorStatus={dorStatus}
        lastSubmissionDate={lastSubmissionDate}
        showClaimedTag={showClaimedTag}
        showDeadline={showDeadline}
        showDetailsButton={hasShipmentDetailsAccess}
      >
        {showDORButton && (
          <DORButtonDropdown
            shipmentId={id}
            recipientEmail={customer.email || ''}
            trackingCode={trackingCode}
            availableDORLanguages={availableDORLanguages}
            type={typeOfDORButton}
          />
        )}
        {showClaimButton && (
          <PlainButton icon={ClaimsIcon} onClick={onClaim}>
            Submit a claim
          </PlainButton>
        )}
      </ResultHeader>
      <ResultTable>
        <thead styleName="table-header">
          <tr>
            <th>
              <Text>
                <Locale>Creation date</Locale>
              </Text>
            </th>
            <th>
              <Text>
                <Locale>Name</Locale>
              </Text>
            </th>
            <th>
              <Text>
                <Locale>Address</Locale>
              </Text>
            </th>
            <th>
              <Text>
                <Locale>Email</Locale>
              </Text>
            </th>
            <th>
              <Text>
                <Locale>Carrier</Locale>
              </Text>
            </th>
            <th>
              <Text>
                <Locale>Carrier country</Locale>
              </Text>
            </th>
          </tr>
        </thead>
        <tbody styleName="table-body">
          <tr>
            <td>
              <Text ink>{formatDate(creationDate)}</Text>
            </td>
            <td>
              <Text ink>{customer.name}</Text>
            </td>
            <td>
              <Text ink>{customer.address}</Text>
            </td>
            <td>
              <Text ink>{customer.email}</Text>
            </td>
            <td>
              <Text ink>{carrier.name}</Text>
            </td>
            <td>
              <Text ink>{carrier.countryName}</Text>
            </td>
          </tr>
        </tbody>
      </ResultTable>
    </ResultCard>
  );
}

ShipmentCard.propTypes = {
  id: PropTypes.number,
  creationDate: PropTypes.string,
  trackingCode: PropTypes.string,
  customer: PropTypes.shape({
    name: PropTypes.string,
    address: PropTypes.string,
    email: PropTypes.string,
  }),
  lastEvent: PropTypes.shape({
    type: PropTypes.string,
    meaning: PropTypes.oneOf(['really_bad', 'bad', 'good', 'neutral']),
  }),
  carrier: PropTypes.shape({
    name: PropTypes.string,
    countryName: PropTypes.string,
  }),
  onClaim: PropTypes.func,
  claimable: PropTypes.bool,
};

export default React.memo(ShipmentCard);
